import React, { useState, useEffect, useMemo } from 'react';
import { Box, Text, Link } from '@primer/react';
// Import the DSA data
import dsaPatterns from '../data/dsaData.js';

const DSAGuide = () => {
    // State management
    const [searchTerm, setSearchTerm] = useState('');
    const [activeDifficulties, setActiveDifficulties] = useState(new Set([1, 2, 3]));
    const [activeCategories, setActiveCategories] = useState(new Set());
    const [expandedSections, setExpandedSections] = useState(new Set()); // Keep closed by default
    const [expandedSubsections, setExpandedSubsections] = useState(new Set())
    const [expandedCode, setExpandedCode] = useState(new Set());
    const [allAlgorithms, setAllAlgorithms] = useState([]);
    const [totalStats, setTotalStats] = useState({ algorithms: 0, categories: 0 });
    const [allCategories, setAllCategories] = useState([]);
    const [showCategoryModal, setShowCategoryModal] = useState(false);

    // Build search index and calculate stats
    useEffect(() => {
        const algorithms = [];
        const categories = [];
        let algorithmCount = 0;
        let categoryCount = 0;

        for (const [categoryName, categoryData] of Object.entries(dsaPatterns.categories)) {
            categoryCount++;
            categories.push(categoryName);
            for (const [subcategoryName, subcategoryData] of Object.entries(categoryData.subcategories)) {
                for (const [algorithmName, algorithmData] of Object.entries(subcategoryData.algorithms)) {
                    algorithms.push({
                        name: algorithmName,
                        category: categoryName,
                        subcategory: subcategoryName,
                        data: algorithmData
                    });
                    algorithmCount++;
                }
            }
        }

        setAllAlgorithms(algorithms);
        setAllCategories(categories);
        setActiveCategories(new Set(categories)); // Initialize with all categories selected
        setTotalStats({ algorithms: algorithmCount, categories: categoryCount });
    }, []);

    // Filter algorithms based on search, difficulty, and category
    const filteredAlgorithms = useMemo(() => {
        if (!searchTerm && activeDifficulties.size === 3 && activeCategories.size === allCategories.length) {
            return allAlgorithms;
        }

        return allAlgorithms.filter(algo => {
            const difficultyMatch = activeDifficulties.has(algo.data.difficulty);
            const categoryMatch = activeCategories.has(algo.category);
            
            if (!searchTerm) return difficultyMatch && categoryMatch;

            const nameMatch = algo.name.toLowerCase().includes(searchTerm.toLowerCase());
            
            // Check keywords
            let keywordMatch = false;
            if (dsaPatterns.patternKeywords && dsaPatterns.patternKeywords.keywords) {
                const patternData = dsaPatterns.patternKeywords.keywords[algo.name];
                if (patternData && patternData.keywords) {
                    keywordMatch = patternData.keywords.toLowerCase().includes(searchTerm.toLowerCase());
                }
            }
            
            return (nameMatch || keywordMatch) && difficultyMatch && categoryMatch;
        });
    }, [allAlgorithms, searchTerm, activeDifficulties, activeCategories, allCategories.length]);

    // Create filtered data structure
    const filteredData = useMemo(() => {
        const data = { categories: {} };
        
        filteredAlgorithms.forEach(algo => {
            if (!data.categories[algo.category]) {
                data.categories[algo.category] = {
                    sectionIcon: dsaPatterns.categories[algo.category].sectionIcon,
                    patternCount: 0,
                    subcategories: {}
                };
            }
            
            const category = data.categories[algo.category];
            
            if (!category.subcategories[algo.subcategory]) {
                category.subcategories[algo.subcategory] = {
                    icon: dsaPatterns.categories[algo.category].subcategories[algo.subcategory].icon,
                    algorithms: {}
                };
            }
            
            category.subcategories[algo.subcategory].algorithms[algo.name] = algo.data;
        });
        
        // Update pattern counts
        Object.values(data.categories).forEach(category => {
            category.patternCount = Object.values(category.subcategories).reduce((total, subcategory) => {
                return total + Object.keys(subcategory.algorithms).length;
            }, 0);
        });
        
        return data;
    }, [filteredAlgorithms]);

    // Helper functions
    const highlightText = (text, searchTerm) => {
        if (!searchTerm) return text;
        
        const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);
        
        return parts.map((part, index) => 
            regex.test(part) ? (
                <span key={index} className="search-highlight">{part}</span>
            ) : part
        );
    };

    const getAlgorithmKeywords = (algorithmName) => {
        if (!dsaPatterns.patternKeywords || !dsaPatterns.patternKeywords.keywords) {
            return null;
        }
        
        const patternData = dsaPatterns.patternKeywords.keywords[algorithmName];
        if (!patternData || !patternData.keywords) return null;
        
        return (
            <div className="algorithm-keywords">
                <strong>🎯 Pattern Keywords:</strong> {patternData.keywords}
            </div>
        );
    };

    const getDifficultyLabel = (difficulty) => {
        switch (difficulty) {
            case 1: return 'Core';
            case 2: return 'Advanced';
            case 3: return 'Expert';
            default: return 'Unknown';
        }
    };

    // Event handlers
    const toggleSection = (sectionName) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sectionName)) {
                newSet.delete(sectionName);
            } else {
                newSet.add(sectionName);
            }
            return newSet;
        });
    };

    const toggleSubsection = (key) => {
        setExpandedSubsections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    const toggleCode = (key) => {
        setExpandedCode(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    const handleDifficultyChange = (difficulty) => {
        setActiveDifficulties(prev => {
            const newSet = new Set(prev);
            if (newSet.has(difficulty)) {
                newSet.delete(difficulty);
            } else {
                newSet.add(difficulty);
            }
            if (newSet.size === 0) {
                return new Set([1, 2, 3]);
            }
            return newSet;
        });
    };

    const handleCategoryChange = (category) => {
        setActiveCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(category)) {
                newSet.delete(category);
            } else {
                newSet.add(category);
            }
            if (newSet.size === 0) {
                return new Set(allCategories);
            }
            return newSet;
        });
    };

    const toggleAllCategories = () => {
        if (activeCategories.size === allCategories.length) {
            setActiveCategories(new Set()); // Deselect all
        } else {
            setActiveCategories(new Set(allCategories)); // Select all
        }
    };

    const openAllCategories = () => {
        setExpandedSections(new Set(Object.keys(filteredData.categories)));
        const allSubsections = new Set();
        Object.entries(filteredData.categories).forEach(([categoryName, categoryData]) => {
            Object.keys(categoryData.subcategories).forEach(subcategoryName => {
                allSubsections.add(`${categoryName}-${subcategoryName}`);
            });
        });
        setExpandedSubsections(allSubsections);
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (showCategoryModal) {
                    setShowCategoryModal(false);
                    return;
                }
                if (searchTerm) {
                    setSearchTerm('');
                    return;
                }
                // Close all open code sections
                setExpandedCode(new Set());
            }
            
            // Ctrl/Cmd + K to focus search
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                const searchInput = document.getElementById('algorithmSearch');
                if (searchInput) {
                    searchInput.focus();
                    searchInput.select();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [searchTerm, showCategoryModal]);

    return (
        <Box mb={6}>
            <style>{`
                /* DSA Guide Specific Styles */
                .search-highlight {
                    background: linear-gradient(135deg, #fef3c7, #fde68a);
                    padding: 0.2rem 0.4rem;
                    border-radius: 4px;
                    font-weight: 600;
                    color: #92400e;
                }
                
                .dsa-content {
                    display: grid;
                    gap: 0;
                    width: 100%;
                    overflow: visible;
                }
                
                @media (max-width: 768px) {
                    .dsa-content {
                        gap: 0.5rem;
                        overflow: visible;
                    }
                }
                
                .section {
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 20px;
                    padding: 2rem;
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    margin-bottom: 0.05rem;
                }
                
                @media (max-width: 768px) {
                    .section {
                        padding: 0.75rem !important;
                        margin-bottom: 0.5rem !important;
                        border-radius: 12px !important;
                    }
                }
                
                .section-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 1.5rem;
                    padding: 1rem 1.5rem;
                    background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
                    border-radius: 12px;
                    color: white;
                    box-shadow: 0 8px 25px rgba(79, 70, 229, 0.3);
                    cursor: pointer;
                    user-select: none;
                    transition: all 0.3s ease;
                }
                
                .section-header:hover {
                    transform: translateY(-1px);
                }
                
                .section-icon {
                    font-size: 1.5rem;
                    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
                }
                
                .section-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    letter-spacing: -0.025em;
                }
                
                .section-count {
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 20px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.875rem;
                    font-weight: 600;
                    margin-left: auto;
                }
                
                .toggle-icon {
                    transition: transform 0.3s ease;
                    margin-left: 0.5rem;
                    transform: rotate(0deg);
                }
                
                .section-header.active .toggle-icon {
                    transform: rotate(180deg);
                }
                
                .section-content {
                    display: none;
                    animation: slideDown 0.3s ease;
                }
                
                .section-content.active {
                    display: block;
                }
                
                .subsection {
                    margin-bottom: 1rem;
                }
                
                @media (max-width: 768px) {
                    .subsection {
                        margin-bottom: 0.25rem !important;
                    }
                    
                    .subsection:last-child {
                        margin-bottom: 0 !important;
                    }
                }
                
                .subsection-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 0.5rem;
                    margin-bottom: 1rem;
                    padding: 0.5rem 1rem;
                    background: linear-gradient(135deg, #059669 0%, #047857 100%);
                    border-radius: 8px;
                    color: white;
                    box-shadow: 0 4px 15px rgba(5, 150, 105, 0.3);
                    cursor: pointer;
                    transition: all 0.3s ease;
                    user-select: none;
                }
                
                .subsection-header:hover {
                    background: linear-gradient(135deg, #047857 0%, #065f46 100%);
                    transform: translateY(-1px);
                    box-shadow: 0 6px 20px rgba(5, 150, 105, 0.4);
                }
                
                .subsection-toggle {
                    font-size: 1.2rem;
                    font-weight: bold;
                    transition: transform 0.3s ease;
                    transform: rotate(-90deg);
                }
                
                .subsection-toggle.expanded {
                    transform: rotate(0deg);
                }
                
                .subsection-content {
                    overflow: hidden;
                    transition: max-height 0.3s ease-out, opacity 0.3s ease-out;
                    max-height: 0;
                    opacity: 0;
                    margin: 0;
                    padding: 0;
                }
                
                .subsection-content.expanded {
                    max-height: none;
                    opacity: 1;
                    overflow: visible;
                }
                
                @media (max-width: 768px) {
                    .subsection-content {
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    
                    .subsection-content.expanded {
                        max-height: none !important;
                        overflow: visible !important;
                        margin: 0 !important;
                        padding: 0.5rem 0 !important;
                    }
                }
                
                .subsection-icon {
                    font-size: 1rem;
                }
                
                .subsection-title {
                    font-size: 1rem;
                    font-weight: 600;
                }
                
                .patterns-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 1.5rem;
                    margin-bottom: 2rem;
                }
                
                @media (max-width: 768px) {
                    .patterns-grid {
                        grid-template-columns: 1fr !important;
                        gap: 0.75rem !important;
                        margin: 0 !important;
                        width: 100% !important;
                        overflow: hidden !important;
                        padding: 0 !important;
                    }
                    
                    .section {
                        margin-bottom: 0.75rem !important;
                        width: 100% !important;
                        overflow: visible !important;
                    }
                    
                    .section-header {
                        flex-wrap: wrap !important;
                        gap: 0.5rem !important;
                        width: 100% !important;
                    }
                    
                    .subsection-content {
                        padding: 0 !important;
                        width: 100% !important;
                        overflow: hidden !important;
                        max-height: 0 !important;
                        box-sizing: border-box !important;
                        margin: 0 !important;
                        opacity: 0 !important;
                    }
                    
                    .subsection-content.expanded {
                        max-height: none !important;
                        overflow: visible !important;
                        padding: 0.5rem 0 !important;
                        opacity: 1 !important;
                        margin: 0 !important;
                    }
                    
                    .pattern-card {
                        width: 100% !important;
                        margin: 0 0 1rem 0 !important;
                        padding: 0.75rem !important;
                        box-sizing: border-box !important;
                        overflow: hidden !important;
                        max-width: 100% !important;
                        word-wrap: break-word !important;
                    }
                    
                    .pattern-meta {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        gap: 0.25rem !important;
                        width: 100% !important;
                        max-width: 100% !important;
                    }
                    
                    .pattern-header {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        gap: 0.5rem !important;
                        width: 100% !important;
                        max-width: 100% !important;
                    }
                    
                    .algorithm-keywords,
                    .pattern-use-case,
                    .key-points {
                        margin: 0.5rem 0 !important;
                        padding: 0.5rem !important;
                        font-size: 0.8rem !important;
                        word-wrap: break-word !important;
                        overflow-wrap: break-word !important;
                        max-width: 100% !important;
                        box-sizing: border-box !important;
                    }
                    
                    .code-toggle {
                        font-size: 0.8rem !important;
                        padding: 0.6rem 1rem !important;
                    }
                    
                    .difficulty-badge {
                        align-self: flex-start !important;
                        margin-top: 0.5rem !important;
                    }
                }
                
                .pattern-card {
                    background: white;
                    border-radius: 16px;
                    padding: 2rem;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
                    border: 1px solid rgba(0,0,0,0.05);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                    overflow: hidden;
                }
                
                .pattern-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, #3b82f6, #8b5cf6);
                }
                
                .pattern-card.difficulty-1::before {
                    background: #22c55e;
                }
                
                .pattern-card.difficulty-2::before {
                    background: #f59e0b;
                }
                
                .pattern-card.difficulty-3::before {
                    background: #ef4444;
                }
                
                .pattern-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 40px rgba(0,0,0,0.15);
                }
                
                .pattern-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 1.5rem;
                }
                
                .pattern-title {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #1a202c;
                    margin-bottom: 0.75rem;
                    line-height: 1.2;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    max-width: 100%;
                }
                
                @media (max-width: 768px) {
                    .pattern-title {
                        font-size: 1.1rem !important;
                        margin-bottom: 0.5rem !important;
                        line-height: 1.3 !important;
                    }
                }
                
                .pattern-meta {
                    display: flex;
                    gap: 0.75rem;
                    flex-wrap: wrap;
                    margin-bottom: 1rem;
                }
                
                .pattern-complexity,
                .pattern-space {
                    background: #f7fafc;
                    color: #4a5568;
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.875rem;
                    font-weight: 600;
                    border: 1px solid #e2e8f0;
                }
                
                .pattern-complexity {
                    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
                    color: #1e40af;
                    border-color: #bfdbfe;
                }
                
                .pattern-space {
                    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
                    color: #166534;
                    border-color: #bbf7d0;
                }
                
                .pattern-description {
                    color: #374151;
                    margin-bottom: 1rem;
                    line-height: 1.6;
                    font-size: 0.9rem;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    max-width: 100%;
                }
                
                .pattern-description strong {
                    color: #111827;
                    font-weight: 600;
                }
                
                .pattern-section {
                    margin-bottom: 0.8rem;
                    font-size: 0.9rem;
                    line-height: 1.5;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    max-width: 100%;
                }
                
                .pattern-section strong {
                    color: #111827;
                    font-weight: 600;
                }
                
                @media (max-width: 768px) {
                    .pattern-description {
                        font-size: 0.8rem !important;
                        margin-bottom: 0.5rem !important;
                    }
                    
                    .pattern-section {
                        font-size: 0.8rem !important;
                        margin-bottom: 0.5rem !important;
                    }
                }
                
                .pattern-use-case {
                    background: linear-gradient(135deg, #fef3c7, #fde68a);
                    padding: 1rem;
                    border-radius: 8px;
                    margin: 1rem 0;
                    font-size: 0.9rem;
                    color: #92400e;
                    line-height: 1.5;
                }
                
                .key-points {
                    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
                    padding: 1rem;
                    border-radius: 8px;
                    margin: 1rem 0;
                    font-size: 0.9rem;
                    color: #1e40af;
                    line-height: 1.5;
                }
                
                .code-section {
                    margin-top: 1.5rem;
                    border-top: 1px solid #e2e8f0;
                    padding-top: 1.5rem;
                }
                
                .code-toggle {
                    background: linear-gradient(135deg, #4f46e5, #7c3aed);
                    color: white;
                    border: none;
                    padding: 0.75rem 1.5rem;
                    border-radius: 8px;
                    font-size: 0.9rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    width: 100%;
                    margin-bottom: 1rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                }
                
                .code-toggle:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 20px rgba(79, 70, 229, 0.4);
                }
                
                .code-toggle.active {
                    background: linear-gradient(135deg, #dc2626, #b91c1c);
                }
                
                .code-content {
                    display: none;
                    animation: slideDown 0.3s ease;
                }
                
                .code-content.active {
                    display: block;
                }
                
                .code-snippet {
                    background: #1a202c;
                    color: #e2e8f0;
                    padding: 1.5rem;
                    border-radius: 12px;
                    font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', monospace;
                    font-size: 0.875rem;
                    line-height: 1.6;
                    overflow-x: auto;
                    border: 1px solid #2d3748;
                    margin-bottom: 1rem;
                    box-shadow: inset 0 2px 8px rgba(0,0,0,0.3);
                    white-space: pre;
                }
                
                .algorithm-keywords {
                    background: linear-gradient(135deg, #f3e8ff, #e9d5ff);
                    padding: 1rem;
                    border-radius: 8px;
                    margin: 1rem 0;
                    font-size: 0.9rem;
                    color: #7c3aed;
                    line-height: 1.5;
                    border: 1px solid #e9d5ff;
                }
                
                .algorithm-keywords strong {
                    color: #6b21a8;
                    font-weight: 600;
                }
                
                .difficulty-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.25rem 0.5rem;
                    border-radius: 9999px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                }
                
                .difficulty-badge-1 {
                    background-color: #dcfce7;
                    color: #166534;
                }
                
                .difficulty-badge-2 {
                    background-color: #fef3c7;
                    color: #92400e;
                }
                
                .difficulty-badge-3 {
                    background-color: #fecaca;
                    color: #991b1b;
                }
                
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                /* Enhanced mobile interactions */
                @media (max-width: 768px) {
                    .filter-section {
                        padding: 1rem;
                    }
                    
                    /* Improve touch targets - exclude checkboxes */
                    button, .clickable-filter {
                        min-height: 44px;
                        min-width: 44px;
                    }
                    
                    /* Keep checkboxes at normal size */
                    input[type="checkbox"] {
                        width: 16px;
                        height: 16px;
                        min-width: 16px;
                        min-height: 16px;
                    }
                    
                    /* Better spacing on mobile */
                    .category-grid {
                        gap: 0.75rem;
                    }
                    
                    /* Enhanced visual feedback */
                    .filter-button:active {
                        transform: scale(0.95);
                        transition: transform 0.1s ease;
                    }
                }
                
                /* Accessibility improvements */
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: 0.01ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: 0.01ms !important;
                    }
                }
                
                /* Focus indicators for keyboard navigation */
                button:focus-visible, input:focus-visible {
                    outline: 2px solid #6366f1;
                    outline-offset: 2px;
                }
                
                /* Modal styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    backdrop-filter: blur(4px);
                    animation: fadeIn 0.2s ease-out;
                }
                
                .modal-content {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
                    max-width: 90vw;
                    max-height: 80vh;
                    overflow: hidden;
                    animation: slideUp 0.3s ease-out;
                    border: 1px solid #e2e8f0;
                }
                
                .modal-header {
                    padding: 1.5rem;
                    border-bottom: 1px solid #e2e8f0;
                    background: #f8fafc;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .modal-body {
                    padding: 1.5rem;
                    overflow-y: auto;
                    max-height: 60vh;
                }
                
                .modal-footer {
                    padding: 1rem 1.5rem;
                    border-top: 1px solid #e2e8f0;
                    background: #f8fafc;
                    display: flex;
                    gap: 1rem;
                    justify-content: flex-end;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                @keyframes slideUp {
                    from { 
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                
                @media (max-width: 768px) {
                    .modal-content {
                        margin: 1rem;
                        max-width: calc(100vw - 2rem);
                        max-height: calc(100vh - 2rem);
                    }
                    
                    .modal-header {
                        padding: 1rem;
                    }
                    
                    .modal-body {
                        padding: 1rem;
                        max-height: calc(70vh - 4rem);
                    }
                    
                    .modal-footer {
                        padding: 1rem;
                        flex-direction: column;
                    }
                }
            `}</style>
            
            {/* Introduction Section */}
            <Box mb={4}>
                <Box
                    bg="accent.subtle"
                    p={4}
                    borderRadius={2}
                    border="1px solid"
                    borderColor="accent.muted"
                    mb={4}
                >
                    <Text
                        sx={{
                            fontSize: [1, 2],
                            color: 'accent.fg',
                            lineHeight: 1.5
                        }}
                    >
                        <strong>What is this?</strong> This is a little something I made for myself to help me study and keep my DSA skills sharp, after running on <u><i><Link href="https://www.algomonster.com/flowchart" sx={{ color: 'accent.fg' }} target="_blank">AlgoMonster's Flowchart</Link></i></u> multiple times and loving it. It's a work in progress and I'm constantly updating it with new algorithms and patterns, but since it helped me so much, I decided to share it with the world. You're welcome.
                    </Text>
                </Box>
            </Box>

            <Box mb={4}>
                <Box
                    bg="attention.subtle"
                    p={4}
                    borderRadius={2}
                    border="1px solid"
                    borderColor="attention.muted"
                    mb={4}
                >
                    <Text
                        sx={{
                            fontSize: [1, 2],
                            color: 'attention.fg',
                            lineHeight: 1.5
                        }}
                    >
                        <strong>Please note that</strong>, although I did my best to make this as accurate as possible, I might have missed some details or forgotten some algorithms, so if you see any mistakes or have any suggestions, please let me know!
                    </Text>
                </Box>
            </Box>

            {/* Search Section */}
            <Box
                bg="canvas.subtle"
                p={4}
                borderRadius={2}
                border="1px solid"
                borderColor="border.default"
                mb={4}
                className="search-section"
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}
            >
                {/* Filters Section - Enhanced for Mobile/Desktop */}
                <Box mb={3}>
                    {/* Difficulty Filters */}
                    <Box mb={3}>
                        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
                            <Text fontSize={1} fontWeight="semibold" color="fg.muted">
                                Difficulty:
                            </Text>
                            <Box display="flex" gap={2} flexWrap="wrap">
                            {[1, 2, 3].map(difficulty => {
                                const label = getDifficultyLabel(difficulty);
                                const isActive = activeDifficulties.has(difficulty);
                                
                                return (
                                    <Box
                                        key={difficulty}
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                                opacity: 0.8
                                            }
                                        }}
                                        onClick={() => handleDifficultyChange(difficulty)}
                                    >
                                        <Box
                                            as="input"
                                            type="checkbox"
                                            checked={isActive}
                                            onChange={() => handleDifficultyChange(difficulty)}
                                            sx={{
                                                accentColor: difficulty === 1 ? '#22c55e' : difficulty === 2 ? '#f59e0b' : '#ef4444',
                                                cursor: 'pointer'
                                            }}
                                        />
                                        <Text
                                            fontSize={[0, 1]}
                                            fontWeight="semibold"
                                            color={difficulty === 1 ? 'success.fg' : difficulty === 2 ? 'attention.fg' : 'danger.fg'}
                                            sx={{ 
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {label}
                                        </Text>
                                    </Box>
                                );
                            })}
                            </Box>
                        </Box>
                    </Box>
                    
                    {/* Category Filter Button */}
                    <Box mb={2}>
                        <Box
                            as="button"
                            onClick={() => setShowCategoryModal(true)}
                            bg="success.subtle"
                            color="success.fg"
                            border="1px solid"
                            borderColor="success.muted"
                            px={3}
                            py={1}
                            borderRadius={2}
                            fontSize={1}
                            fontWeight={500}
                            sx={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                                '&:hover': {
                                    bg: 'success.muted',
                                    borderColor: 'success.emphasis',
                                    transform: 'translateY(-1px)'
                                },
                                '&:active': {
                                    transform: 'scale(0.98)'
                                }
                            }}
                        >
                            <Box as="span" fontSize={2}>🏷️</Box>
                            <Box as="span">Categories ({activeCategories.size})</Box>
                        </Box>
                    </Box>
                </Box>

                {/* Search Input Field - Enhanced for Mobile */}
                <Box mb={3}>
                    <Box
                        display="flex"
                        alignItems="center"
                        bg="canvas.default"
                        border="2px solid"
                        borderColor="border.default"
                        borderRadius={2}
                        px={[2, 3, 3]}
                        py={[2, 2, 2]}
                        sx={{
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.2s ease',
                            minHeight: ['44px', '48px', '52px'], // Touch-friendly height
                            '&:focus-within': {
                                borderColor: 'accent.emphasis',
                                boxShadow: '0 0 0 2px rgba(99, 102, 241, 0.1)'
                            }
                        }}
                    >
                        <Text mr={[1, 2, 2]} fontSize={[2, 2, 3]}>🔍</Text>
                        <Box
                            as="input"
                            type="text"
                            id="algorithmSearch"
                            placeholder={`Search through ${activeCategories.size === allCategories.length && activeDifficulties.size === 3 ? totalStats.algorithms : filteredAlgorithms.length} algorithms...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{
                                flex: 1,
                                border: 'none',
                                outline: 'none',
                                fontSize: [2, 2, 3],
                                bg: 'transparent',
                                minWidth: 0,
                                width: '100%',
                                '&::placeholder': {
                                    color: 'fg.muted',
                                    opacity: 0.7
                                }
                            }}
                        />
                        {/* Clear button for mobile */}
                        {searchTerm && (
                            <Box
                                as="button"
                                onClick={() => setSearchTerm('')}
                                ml={2}
                                p={1}
                                bg="transparent"
                                border="none"
                                borderRadius={1}
                                sx={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '24px',
                                    minHeight: '24px',
                                    fontSize: 2,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bg: 'danger.subtle',
                                        color: 'danger.fg'
                                    }
                                }}
                                title="Clear search"
                            >
                                ✕
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Open All Categories Button - Enhanced for Mobile */}
                <Box display="flex" justifyContent="center" px={[2, 0, 0]}>
                    <Box
                        as="button"
                        onClick={openAllCategories}
                        bg="accent.subtle"
                        color="accent.fg"
                        border="1px solid"
                        borderColor="accent.muted"
                        px={[3, 4, 4]}
                        py={[2, 2, 2]}
                        borderRadius={2}
                        fontSize={[1, 1, 1]}
                        fontWeight={500}
                        width={["100%", "auto", "auto"]}
                        sx={{
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            textDecoration: 'none',
                            whiteSpace: 'nowrap',
                            minHeight: '44px', // Touch-friendly
                            maxWidth: ['100%', '300px', '300px'],
                            '&:hover': {
                                bg: 'accent.muted',
                                borderColor: 'accent.emphasis',
                                textDecoration: 'none'
                            },
                            '&:active': {
                                transform: 'scale(0.98)'
                            }
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={2}>
                            📂 Open All Categories
                        </Box>
                    </Box>
                </Box>

                {/* Search Results Display - Enhanced for Mobile */}
                {searchTerm && (
                    <Box
                        bg="accent.subtle"
                        p={[2, 3, 3]}
                        borderRadius={1}
                        border="1px solid"
                        borderColor="accent.muted"
                        mt={3}
                        sx={{
                            overflow: 'hidden'
                        }}
                    >
                        <Text color="accent.fg" fontSize={[1, 1, 1]} mb={1}>
                            <strong>Search Results ({filteredAlgorithms.length}):</strong>
                        </Text>
                        <Box
                            sx={{
                                maxHeight: ['120px', '150px', '200px'],
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                wordWrap: 'break-word',
                                lineHeight: 1.5
                            }}
                        >
                            <Text color="accent.fg" fontSize={[0, 1, 1]}>
                                {filteredAlgorithms.map(algo => algo.name).join(', ')}
                            </Text>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Main Content */}
            <Box className="dsa-content">
                {Object.keys(filteredData.categories).length === 0 ? (
                    <Box textAlign="center" p={6} color="fg.muted">
                        <Text as="h3" fontSize={3} color="fg.default" mb={2}>No algorithms found</Text>
                        <Text>
                            {searchTerm 
                                ? `No algorithms match "${searchTerm}". Try a different search term.` 
                                : 'No algorithms match the selected difficulty levels.'
                            }
                        </Text>
                        {searchTerm && (
                            <Text mt={2} fontStyle="italic">Tip: Try searching for partial names like "Kadane", "Binary", or "DFS"</Text>
                        )}
                    </Box>
                ) : (
                    Object.entries(filteredData.categories).map(([categoryName, categoryData]) => {
                        const isSectionExpanded = expandedSections.has(categoryName);
                        
                        return (
                            <div key={categoryName} className="section">
                                <div 
                                    className={`section-header ${isSectionExpanded ? 'active' : ''}`}
                                    onClick={() => toggleSection(categoryName)}
                                >
                                    <span className="section-icon">{categoryData.sectionIcon}</span>
                                    <h2 className="section-title">{categoryName}</h2>
                                    <span className="section-count">{categoryData.patternCount} patterns</span>
                                    <span className="toggle-icon">▼</span>
                                </div>
                                
                                {isSectionExpanded && (
                                    <div className="section-content active">
                                        {Object.entries(categoryData.subcategories).map(([subcategoryName, subcategoryData]) => {
                                            const subsectionKey = `${categoryName}-${subcategoryName}`;
                                            const isSubsectionExpanded = expandedSubsections.has(subsectionKey);
                                            
                                            return (
                                                <div key={subcategoryName} className="subsection">
                                                    <div 
                                                        className="subsection-header"
                                                        onClick={() => toggleSubsection(subsectionKey)}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                            <span className="subsection-icon">{subcategoryData.icon}</span>
                                                            <h3 className="subsection-title">{subcategoryName}</h3>
                                                        </div>
                                                        <span className={`subsection-toggle ${isSubsectionExpanded ? 'expanded' : ''}`}>
                                                            ▼
                                                        </span>
                                                    </div>
                                                    
                                                    <div className={`subsection-content ${isSubsectionExpanded ? 'expanded' : ''}`}>
                                                        <div className="patterns-grid">
                                                            {Object.entries(subcategoryData.algorithms).map(([algorithmName, algorithmData]) => {
                                                                const difficultyLabel = getDifficultyLabel(algorithmData.difficulty);
                                                                const codeKey = `${categoryName}-${subcategoryName}-${algorithmName}`;
                                                                const isCodeExpanded = expandedCode.has(codeKey);
                                                                
                                                                return (
                                                                    <div 
                                                                        key={algorithmName} 
                                                                        className={`pattern-card difficulty-${algorithmData.difficulty}`}
                                                                    >
                                                                        <div className="pattern-header">
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', width: '100%' }}>
                                                                                <div>
                                                                                    <h4 className="pattern-title">
                                                                                        {highlightText(algorithmName, searchTerm)}
                                                                                    </h4>
                                                                                    <div className="pattern-meta">
                                                                                        <span className="pattern-complexity">
                                                                                            Time: {algorithmData.complexity}
                                                                                        </span>
                                                                                        <span className="pattern-space">
                                                                                            Space: {algorithmData.spaceComplexity || 'O(1)'}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                                <span className={`difficulty-badge difficulty-badge-${algorithmData.difficulty}`}>
                                                                                    ● {difficultyLabel}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        <div className="pattern-section">
                                                                            <strong>What it does:</strong> {algorithmData.description}
                                                                        </div>
                                                                        
                                                                        <div className="pattern-section">
                                                                            <strong>How it works:</strong> {algorithmData.howItWorks}
                                                                        </div>
                                                                        
                                                                        <div className="pattern-section">
                                                                            <strong>Why use it:</strong> {algorithmData.whyUseIt}
                                                                        </div>
                                                                        
                                                                        <div className="pattern-use-case">
                                                                            <strong>Use when:</strong> {algorithmData.useWhen}
                                                                        </div>
                                                                        
                                                                        <div className="key-points">
                                                                            <strong>🔑 Key Points:</strong> {algorithmData.keyPoints}
                                                                        </div>
                                                                        
                                                                        {getAlgorithmKeywords(algorithmName)}
                                                                        
                                                                        <div className="code-section">
                                                                            <button 
                                                                                className={`code-toggle ${isCodeExpanded ? 'active' : ''}`}
                                                                                onClick={() => toggleCode(codeKey)}
                                                                            >
                                                                                <span>{isCodeExpanded ? '👁️' : '👨‍💻'}</span>
                                                                                {isCodeExpanded ? 'Hide Code Implementation' : 'Show Code Implementation'}
                                                                            </button>
                                                                            
                                                                            <div className={`code-content ${isCodeExpanded ? 'active' : ''}`}>
                                                                                <div className="code-snippet">
                                                                                    {algorithmData.codeExample}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </Box>

            {/* Category Selection Modal */}
            {showCategoryModal && (
                <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <Box display="flex" alignItems="center" gap={2}>
                                <Text fontSize={3} fontWeight="bold" color="fg.default">
                                    🏷️ Select Categories
                                </Text>
                                <Box 
                                    bg="success.emphasis" 
                                    color="canvas.default" 
                                    px={2} 
                                    py={1} 
                                    borderRadius={1} 
                                    fontSize={1}
                                    fontWeight="bold"
                                >
                                    {activeCategories.size}/{allCategories.length}
                                </Box>
                            </Box>
                            <Box
                                as="button"
                                onClick={() => setShowCategoryModal(false)}
                                bg="transparent"
                                border="none"
                                color="fg.muted"
                                fontSize={4}
                                sx={{
                                    cursor: 'pointer',
                                    padding: '0.5rem',
                                    borderRadius: '50%',
                                    lineHeight: 1,
                                    '&:hover': {
                                        bg: 'danger.subtle',
                                        color: 'danger.fg'
                                    }
                                }}
                                title="Close"
                            >
                                ✕
                            </Box>
                        </div>
                        
                        <div className="modal-body">
                            {/* Quick Actions */}
                            <Box display="flex" gap={2} mb={4} flexWrap="wrap">
                                <Box
                                    as="button"
                                    onClick={toggleAllCategories}
                                    bg="accent.subtle"
                                    color="accent.fg"
                                    border="1px solid"
                                    borderColor="accent.muted"
                                    px={3}
                                    py={2}
                                    borderRadius={1}
                                    fontSize={1}
                                    fontWeight={500}
                                    sx={{
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        flex: ['1', '0 0 auto'],
                                        minHeight: '40px',
                                        '&:hover': {
                                            bg: 'accent.muted',
                                            borderColor: 'accent.emphasis'
                                        }
                                    }}
                                >
                                    {activeCategories.size === allCategories.length ? 'Deselect All' : 'Select All'}
                                </Box>
                            </Box>
                            
                            {/* Categories Grid */}
                            <Box 
                                display="grid" 
                                gridTemplateColumns={["1fr", "repeat(2, 1fr)"]}
                                gap={3}
                                sx={{
                                    '@media (max-width: 480px)': {
                                        gridTemplateColumns: '1fr'
                                    }
                                }}
                            >
                                {allCategories.map(category => {
                                    const isActive = activeCategories.has(category);
                                    const categoryData = dsaPatterns.categories[category];
                                    const algorithmCount = Object.values(categoryData.subcategories || {}).reduce((total, subcategory) => {
                                        return total + Object.keys(subcategory.algorithms || {}).length;
                                    }, 0);
                                    
                                    return (
                                        <Box
                                            key={category}
                                            bg={isActive ? "success.subtle" : "canvas.subtle"}
                                            border="2px solid"
                                            borderColor={isActive ? "success.emphasis" : "border.default"}
                                            borderRadius={2}
                                            p={3}
                                            sx={{
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    bg: isActive ? 'success.muted' : 'accent.subtle',
                                                    borderColor: isActive ? 'success.emphasis' : 'accent.muted',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                                },
                                                '&:active': {
                                                    transform: 'scale(0.98)'
                                                }
                                            }}
                                            onClick={() => handleCategoryChange(category)}
                                        >
                                            <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                                                <Box
                                                    as="input"
                                                    type="checkbox"
                                                    checked={isActive}
                                                    onChange={() => handleCategoryChange(category)}
                                                    sx={{
                                                        accentColor: '#22c55e',
                                                        cursor: 'pointer',
                                                        width: '18px',
                                                        height: '18px',
                                                        flexShrink: 0,
                                                        marginTop: '2px'
                                                    }}
                                                />
                                                <Box flex="1">
                                                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                                                        <Text fontSize={2}>{categoryData?.sectionIcon}</Text>
                                                        <Text
                                                            fontSize={2}
                                                            fontWeight="semibold"
                                                            color="fg.default"
                                                            sx={{ 
                                                                cursor: 'pointer',
                                                                userSelect: 'none',
                                                                lineHeight: 1.2
                                                            }}
                                                        >
                                                            {category}
                                                        </Text>
                                                    </Box>
                                                    <Text
                                                        fontSize={1}
                                                        color="fg.muted"
                                                        sx={{
                                                            cursor: 'pointer',
                                                            userSelect: 'none'
                                                        }}
                                                    >
                                                        {algorithmCount} algorithm{algorithmCount !== 1 ? 's' : ''}
                                                    </Text>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </div>
                        
                        <div className="modal-footer">
                            <Box
                                as="button"
                                onClick={() => setShowCategoryModal(false)}
                                bg="accent.subtle"
                                color="accent.fg"
                                border="1px solid"
                                borderColor="accent.muted"
                                px={4}
                                py={2}
                                borderRadius={1}
                                fontSize={1}
                                fontWeight={500}
                                sx={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    flex: ['1', '0 0 auto'],
                                    minHeight: '44px',
                                    '&:hover': {
                                        bg: 'accent.muted',
                                        borderColor: 'accent.emphasis'
                                    }
                                }}
                            >
                                Done
                            </Box>
                        </div>
                    </div>
                </div>
            )}
        </Box>
    );
};

export default DSAGuide; 